import type { AccountsView } from "./types";

/**
 * Helpers for the management-accounts trend column (19 §4.7). Words, not verdicts: a figure goes
 * "up" or "down", never "better" or "worse". Whether a rise is welcome depends on the measure.
 */

export type KpiUnit = AccountsView["kpis"][number]["unit"];
export type TrendPoint = { period: string; value: number };

const NUMBER_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];

/** 6 → "six"; numbers beyond twelve stay as digits. */
export function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

/** Rounded the same way the accounts table shows the figure. */
export function roundFor(value: number, unit: KpiUnit): number {
  return unit === "months" ? Math.round(value * 10) / 10 : Math.round(value);
}

const show = (value: number, unit: KpiUnit) => (unit === "months" ? roundFor(value, unit).toFixed(1) : String(roundFor(value, unit)));

export function formatKpi(value: number, unit: KpiUnit): string {
  if (unit === "percent") return `${show(value, unit)}%`;
  if (unit === "days") return `${show(value, unit)} days`;
  return `${show(value, unit)} months`;
}

/** "101 to 122 days", "74% to 76%", "2.4 to 1.7 months". */
function range(a: number, b: number, unit: KpiUnit): string {
  if (unit === "percent") return `${show(a, unit)}% to ${show(b, unit)}%`;
  return `${show(a, unit)} to ${show(b, unit)} ${unit}`;
}

function span(months: number): string {
  return months === 1 ? "over one month" : `over ${numberWord(months)} months`;
}

/**
 * The text alternative for a sparkline, e.g. "Up from 101 to 122 days over six months".
 * Compares the first and last presented months, at the precision the table shows.
 */
export function trendSentence(trend: TrendPoint[], unit: KpiUnit): string {
  const first = trend[0];
  const last = trend[trend.length - 1];
  if (!first || !last) return "No earlier months to compare.";
  if (trend.length === 1) return "Only one month presented so far.";
  const months = trend.length - 1;
  const a = roundFor(first.value, unit);
  const b = roundFor(last.value, unit);
  if (a === b) return `Level at ${formatKpi(b, unit)} ${span(months)}.`;
  return `${b > a ? "Up" : "Down"} from ${range(a, b, unit)} ${span(months)}.`;
}

/** "up 4 days", "down 2 points", "no change"; null when there is no previous month. */
export function changeSinceLastMonth(trend: TrendPoint[], unit: KpiUnit): string | null {
  const prev = trend[trend.length - 2];
  const last = trend[trend.length - 1];
  if (!prev || !last) return null;
  const diff = roundFor(roundFor(last.value, unit) - roundFor(prev.value, unit), unit);
  if (diff === 0) return "no change";
  const size = Math.abs(diff);
  const amount = unit === "percent"
    ? `${size} ${size === 1 ? "point" : "points"}`
    : unit === "days" ? `${size} ${size === 1 ? "day" : "days"}` : `${size.toFixed(1)} months`;
  return `${diff > 0 ? "up" : "down"} ${amount}`;
}

/**
 * SVG polyline points for a sparkline in a width × height box (with a little padding so the stroke
 * and end dot are not clipped). A flat series is drawn along the middle.
 */
export function sparklinePoints(values: number[], width: number, height: number, pad = 3): { x: number; y: number }[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const spread = max - min;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  return values.map((v, i) => ({
    x: round2(pad + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW)),
    y: round2(spread === 0 ? height / 2 : pad + innerH - ((v - min) / spread) * innerH),
  }));
}

const round2 = (n: number) => Math.round(n * 100) / 100;
